// API-source selection, driven by a single committed JSON file (config/api-sources.json at the
// repo root). Every endpoint can be pointed at the real backend or the Postman mock API — one
// line per endpoint; anything not listed uses the file's "default". Applied client-side, per
// request, by the shared axios instance's request interceptor (see ./client.ts), which overrides
// that request's baseURL. Source selection lives in the JSON (checked in, reviewed in diffs); the
// real/mock-api base URLs stay in env (environment-specific). Mirrors greenway_store_front's
// lib/api/source-config.ts, minus the Next.js server-proxy half — this is a CSR-only SPA, so
// there's no route handler to resolve the source in; the axios interceptor is the one seam.

import { env } from '@/lib/env';

import rawApiSources from '../../../config/api-sources.json';

export const API_SOURCES = ['real', 'mock-api'] as const;
export type ApiSource = (typeof API_SOURCES)[number];

export interface ApiSourcesConfig {
  /** Source for any endpoint not listed explicitly below. */
  default: ApiSource;
  /** Per-endpoint source, keyed by the exact `endpoints.*` path (see ./endpoints.ts). */
  endpoints: Record<string, ApiSource>;
}

function isApiSource(value: unknown): value is ApiSource {
  return typeof value === 'string' && (API_SOURCES as readonly string[]).includes(value);
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

/** Validate the raw JSON into a config, dropping any non-source values (incl. the "//" comment keys). */
export function normalizeConfig(raw: unknown): ApiSourcesConfig {
  const obj = asRecord(raw);
  const endpoints: Record<string, ApiSource> = {};
  for (const [path, value] of Object.entries(asRecord(obj.endpoints))) {
    if (isApiSource(value)) endpoints[path] = value;
  }
  return { default: isApiSource(obj.default) ? obj.default : 'real', endpoints };
}

/** The effective config, parsed once from config/api-sources.json at module load. */
export const apiSourcesConfig: ApiSourcesConfig = normalizeConfig(rawApiSources);

/** Resolve the configured source for a backend endpoint path, or the file default. */
export function resolveSource(
  path: string,
  config: ApiSourcesConfig = apiSourcesConfig,
): ApiSource {
  return config.endpoints[path] ?? config.default;
}

function clean(value: string | undefined): string | undefined {
  return value && value.trim() ? value.trim() : undefined;
}

/**
 * The base URL for a source. Both sources fall back to `VITE_API_URL` so each stays resolvable
 * even when its dedicated env var is unset (e.g. a fresh checkout with only .env.example values).
 */
export function baseUrlForSource(source: ApiSource): string {
  const url =
    source === 'real'
      ? (clean(env.VITE_REAL_API_URL) ?? env.VITE_API_URL)
      : (clean(env.VITE_MOCK_API_URL) ?? env.VITE_API_URL);
  return url.replace(/\/$/, '');
}
